import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, Clock, Users, Video, Calendar, Award, CreditCard, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    platform_fee_percentage: 10,
    gst_rate: 18,
    razorpay_fee: 2.5
  });
  
  useEffect(() => {
    fetchCourse();
    checkAuth();
    fetchPlatformSettings();
  }, [id]);
  
  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setPlatformSettings({
        platform_fee_percentage: response.data.platform_fee_percentage || 10,
        gst_rate: response.data.gst_rate || 18,
        razorpay_fee: response.data.razorpay_fee || 2.5
      });
    } catch (error) {
      // Use defaults if settings not available
      console.log('Using default platform settings');
    }
  };
  
  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };
  
  const handleEnroll = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/courses/${id}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    
        await handleRazorpayPayment();
  };
  const handleRazorpayPayment = async () => {
    try {
      setProcessing(true);
      
      // Calculate fees
      const coachPrice = course.price;
      const platformFee = coachPrice * (platformSettings.platform_fee_percentage / 100);
      const gst = platformFee * (platformSettings.gst_rate / 100);
      const studentTotal = coachPrice + platformFee + gst;
      
      // Create Razorpay order with FULL amount (including fees)
      const orderResponse = await axios.post(
        `${API}/payments/razorpay/create-order`,
        { 
          course_id: id,
          amount: studentTotal,
          coach_price: coachPrice,
          platform_fee: platformFee,
          gst_amount: gst
        },
        { withCredentials: true }
      );
      
      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        order_id: orderResponse.data.order_id,
        name: 'DIOS',
        description: course.title,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(
              `${API}/payments/razorpay/verify`,
              {
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                course_id: id,
                coach_price: coachPrice,
                platform_fee: platformFee,
                gst_amount: gst,
                student_total: studentTotal
              },
              { withCredentials: true }
            );
            toast.success('Enrollment successful!');
            navigate('/dashboard');
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#C9A84C'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };
  
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <p className="text-xl text-white/60">Course not found</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark" data-testid="course-detail-page">
      {/* Hero */}
      <div className="bg-dark-surface text-white py-16 border-b border-premium-gold/30/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="flex gap-2 mb-4">
                <span className="inline-block px-3 py-1 bg-premium-gold text-white text-xs font-bold rounded-full">
                  {course.sport_category}
                </span>
                <span className="inline-block px-3 py-1 bg-premium-gold/20 text-premium-gold text-xs font-bold rounded-full border border-premium-gold/30">
                  {course.level}
                </span>
              </div>
              <h1 
                className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
                data-testid="course-title"
              >
                {course.title}{course.age_group && course.age_group !== 'All ages' ? ` (${course.age_group})` : ''}
              </h1>
              <p className="text-lg text-white/70 mb-6" data-testid="course-description">{course.description}</p>
              
              <div className="flex items-center gap-4 mb-6 p-4 bg-deep-dark/50 rounded-xl border border-premium-gold/30/30">
                {course.coach_picture ? (
                  <img src={course.coach_picture} alt={course.coach_name} className="w-16 h-16 rounded-full border-2 border-premium-gold/50" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-deep-dark border-2 border-premium-gold/30"></div>
                )}
                <div>
                  <p className="text-sm text-white/50 font-semibold">Coach</p>
                  <p className="text-lg font-bold text-white">{course.coach_name}</p>
                  {course.coach_rating > 0 && (
                    <div className="flex items-center gap-1 text-premium-gold">
                      <Star className="w-4 h-4 fill-current" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">{course.coach_rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-deep-dark/50 px-4 py-2 rounded-lg border border-premium-gold/30/30">
                  <Clock className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  <span className="text-white/80">{course.duration_weeks} weeks</span>
                </div>
                <div className="flex items-center gap-2 bg-deep-dark/50 px-4 py-2 rounded-lg border border-premium-gold/30/30">
                  <Users className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  <span className="text-white/80">{course.enrolled_count} enrolled</span>
                </div>
                {course.includes_live_sessions && (
                  <div className="flex items-center gap-2 bg-premium-gold/20 px-4 py-2 rounded-lg border border-energy-red/30">
                    <Video className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <span className="text-white/80">Live Sessions</span>
                  </div>
                )}
                {course.includes_recorded_videos && (
                  <div className="flex items-center gap-2 bg-premium-gold/20 px-4 py-2 rounded-lg border border-premium-gold/30">
                    <Calendar className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <span className="text-white/80">Recorded Videos</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Enrollment Card */}
            <div className="bg-dark-surface border-2 border-premium-gold/50 p-8 rounded-xl shadow-card" data-testid="enrollment-card">
              {/* Fee Breakdown */}
              <div className="mb-6 pb-6 border-b border-premium-gold/30">
                <p className="text-sm text-white/60 font-semibold mb-4">Price Breakdown</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Course Fee</span>
                    <span className="text-white font-semibold">₹{course.price.toFixed(0)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">
                      DIOS Platform Fee ({platformSettings.platform_fee_percentage}%)
                    </span>
                    <span className="text-white/80">₹{(course.price * (platformSettings.platform_fee_percentage / 100)).toFixed(0)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">
                      GST on Platform Fee ({platformSettings.gst_rate}%)
                    </span>
                    <span className="text-white/80">₹{((course.price * (platformSettings.platform_fee_percentage / 100)) * (platformSettings.gst_rate / 100)).toFixed(0)}</span>
                  </div>
                  
                  <div className="h-px bg-premium-gold/30 my-3"></div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>Total</span>
                    <span className="text-3xl font-black text-premium-gold" style={{ fontFamily: 'Fraunces, serif' }} data-testid="total-price">
                      ₹{(course.price + (course.price * (platformSettings.platform_fee_percentage / 100)) + ((course.price * (platformSettings.platform_fee_percentage / 100)) * (platformSettings.gst_rate / 100))).toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleEnroll}
                disabled={processing}
                className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-bold tracking-wider px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="enroll-button"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                    Processing...
                  </>
                ) : user ? (
                  <>
                    <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                    Enroll Now
                  </>
                ) : 'Login to Enroll'}
              </button>
              
              <p className="text-xs text-center mt-4 flex items-center justify-center gap-2" style={{ color: '#9B8EC4' }}>
                <Shield className="w-4 h-4" strokeWidth={1.5} />
                Secured by Razorpay
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Coach Bio */}
      {course.coach_bio && (
        <div className="py-12 bg-deep-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-3xl font-bold tracking-tight mb-6 text-white flex items-center gap-3"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              <Award className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
              About the Coach
            </h2>
            <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
              <p className="text-white/80 leading-relaxed">{course.coach_bio}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Reviews */}
      {course.reviews && course.reviews.length > 0 && (
        <div className="py-12 bg-dark-surface border-t border-premium-gold/30/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-3xl font-bold tracking-tight mb-8 text-white flex items-center gap-3"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              <Star className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
              Student Reviews
            </h2>
            <div className="space-y-6">
              {course.reviews.map((review) => (
                <div key={review.review_id} className="bg-deep-dark p-6 rounded-xl border border-premium-gold/30/50" data-testid={`review-${review.review_id}`}>
                  <div className="flex items-center gap-4 mb-3">
                    {review.student_picture ? (
                      <img src={review.student_picture} alt={review.student_name} className="w-12 h-12 rounded-full border-2 border-premium-gold/30" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-dark-surface border-2 border-premium-gold/30"></div>
                    )}
                    <div>
                      <p className="font-bold text-white">{review.student_name}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < review.rating ? 'text-premium-gold fill-current' : 'text-purple-800'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-white/70">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseDetail;
